import React, { useState } from "react";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";

/**
 * bottomblockleft building block (SCRUM-642, VariableField registry) — a FLAT
 * single object with six string fields (no items array, no add/remove, no
 * media picker). Byte-identical port of the three legacy branches pinned by
 * SCRUM-630/640:
 *
 * - Wizard (create): IndexTemplateSelector.jsx lines 2198–2323, ContentOptional
 *   droppable ONLY — there is no ContentRequired wizard branch for this type
 *   (contentVariables.types.test.jsx "bottomblockleft: collapsed by default;
 *   expands to 6 flat fields; reports the {…, righttile} shape (typo key)").
 * - Update required: the former Required host (.jsx) lines 1181–1251
 *   (handleContentRequired.composites.test.jsx "bottomblockleft — …").
 * - Update optional: the former Optional host (.jsx) lines 984–1048
 *   (handleContentOptional.types.test.jsx "bottomblockleft — …"). NOT
 *   character-identical to the required branch — different layout, see below —
 *   hence the extra `compact` prop (deviation from the shared "update covers
 *   both" convention, per the spec's per-surface table).
 * - Stored-at-publish: e2e variableBaselineComposites.spec.ts lines 249–257
 *   (empty fields stored as ""; sixth key is the typo `righttile`). The e2e
 *   page object expands by clicking the exact variable-name label text and
 *   scopes the flat input names by an ancestor whose class contains
 *   "-container" — both are load-bearing.
 *
 * Emits `onChange(settingVar.variable, next)` where `next` is always the
 * COMPLETE fresh 6-key object `{leftlogo, leftvalue, lefttitle, rightlogo,
 * rightvalue, righttile}` (never a partial patch). Host reporting envelopes
 * are step-4 concerns: the wizard adapts to
 * `onChangeVariableValue({target: {name: variable, value: next}})` (keeping
 * its in-place Content mutation + `onContentChange(Content)` effect), the
 * update surfaces to `setVariables` + `props.onVariableChange({...v, value})`.
 *
 * Variant differences (per the spec's per-surface table):
 * - create: `<div className="buttonblockleft-container">` (SECOND typo:
 *   `button…`, not `bottom…`); header Row `varible_container mb-4` whose Label
 *   is simultaneously the drag handle (`summaryProps`, replacing the legacy
 *   `handleProps` spread one-for-one) and the collapse toggle; COLLAPSED by
 *   default with the six field rows UNMOUNTED; reactstrap `Input`, one field
 *   per row (Label md=2 / Col md=10); logo placeholder "Logo URL or path"
 *   (leftlogo + rightlogo only, exactly 2 occurrences when expanded); dead
 *   FormFeedback validation chrome after the rows (`isValidationAttempted`
 *   + `validationError` props — never fires in practice because the wizard
 *   validates ContentRequired only, kept for byte-identity).
 * - update (default, HandleContent (required)): always expanded, container
 *   `bottomblockleft-container`, header `mb-3`, same one-field-per-row
 *   reactstrap layout, logo placeholder "Logo URL", no drag/collapse/feedback.
 * - update + `compact` (HandleContent (optional)): container gains `mb-4`, header
 *   `mb-2`, TWO fields per row (Label md=2 + Col md=4 pairs, 3 rows), RAW
 *   `<input className="form-control form-control-sm">` instead of reactstrap.
 *
 * Preserved quirks (do NOT clean up):
 * - Sixth key is literally `righttile` (missing a `t`); label text says
 *   "Right Title". Pinned by 3 unit suites + the e2e stored shape — live
 *   page_content documents already store this key.
 * - Normalization identical on all surfaces: non-object values (template
 *   default `""`, null) → all-empty object; `|| ''` collapses falsy stored
 *   fields (0, false) to ""; an Array value passes the object check and
 *   yields all-empty fields; unknown extra keys are DROPPED on first edit
 *   (update spreads the normalized value, not settingVar.value).
 * - Flat, non-namespaced input names (`leftlogo`…`righttile`) — page-wide
 *   collisions are resolved by the container scoping, keep the names.
 * - The `varible_container` typo class on every row.
 *
 * Deliberate mechanism swap (documented per the conventions): the wizard host
 * kept collapse state in a `collapsedVars` map keyed by variable name; the
 * block owns it as local `useState` instead. Same observable behaviour:
 * default collapsed (`?? true`), label click toggles, collapsed fields are
 * unmounted. React `key` on the block (legacy `key={index}` on the update
 * branches' div, `key={contentVar.variable}` on the wizard Draggable) is left
 * to the parent.
 */
export type BottomBlockLeftFieldProps = {
  /** the stored page_content value is untyped JSON; this block normalises it below */
  settingVar: { variable?: string; value?: any }; // any: stored page_content value; template defaults are non-object, normalized below to the 6-key object
  /** reports the canonical value object for the variable */
  onChange: (name: string | undefined, value: Record<string, unknown>) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
  validationError?: React.ReactNode;
  isValidationAttempted?: boolean;
  /** HandleContent (optional)'s denser layout (see the header comment) */
  compact?: boolean;
};

export default function BottomBlockLeftField({
  settingVar,
  onChange,
  variant = "update",
  compact = false,
  summaryProps = {},
  validationError,
  isValidationAttempted = false,
}: BottomBlockLeftFieldProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const fieldName = settingVar.variable;

  const value =
    settingVar.value && typeof settingVar.value === "object"
      ? {
          leftlogo: settingVar.value.leftlogo || "",
          leftvalue: settingVar.value.leftvalue || "",
          lefttitle: settingVar.value.lefttitle || "",
          rightlogo: settingVar.value.rightlogo || "",
          rightvalue: settingVar.value.rightvalue || "",
          righttile: settingVar.value.righttile || "",
        }
      : {
          leftlogo: "",
          leftvalue: "",
          lefttitle: "",
          rightlogo: "",
          rightvalue: "",
          righttile: "",
        };

  const setValue = (next) => onChange(fieldName, next);

  const update = (e) => setValue({ ...value, [e.target.name]: e.target.value });

  if (variant === "create") {
    return (
      <div className="buttonblockleft-container">
        <Row className="varible_container mb-4">
          <Label
            md="2"
            className="form-control-label"
            {...summaryProps}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {fieldName}
          </Label>
        </Row>

        {!isCollapsed && (
          <>
            <Row className="varible_container">
              <Label md="2" className="form-control-label">Left Logo</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="leftlogo"
                  placeholder="Logo URL or path"
                  value={value.leftlogo}
                  onChange={update}
                />
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">Left Value</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="leftvalue"
                  value={value.leftvalue}
                  onChange={update}
                />
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">Left Title</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="lefttitle"
                  value={value.lefttitle}
                  onChange={update}
                />
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">Right Logo</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="rightlogo"
                  placeholder="Logo URL or path"
                  value={value.rightlogo}
                  onChange={update}
                />
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">Right Value</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="rightvalue"
                  value={value.rightvalue}
                  onChange={update}
                />
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">Right Title</Label>
              <Col md="10">
                <Input
                  type="text"
                  name="righttile"
                  value={value.righttile}
                  onChange={update}
                />
              </Col>
            </Row>

            {isValidationAttempted && validationError && (
              <Row className="mt-2">
                <Col md={{ offset: 2, size: 10 }}>
                  <FormFeedback className="medium-text d-block">
                    {validationError}
                  </FormFeedback>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bottomblockleft-container mb-4">
        <Row className="varible_container mb-2">
          <Label md="2" className="form-control-label">{fieldName}</Label>
        </Row>
        <Row className="varible_container">
          <Label md="2" className="form-control-label">Left Logo</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="leftlogo" placeholder="Logo URL" value={value.leftlogo} onChange={update} />
          </Col>
          <Label md="2" className="form-control-label">Left Value</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="leftvalue" value={value.leftvalue} onChange={update} />
          </Col>
        </Row>
        <Row className="varible_container">
          <Label md="2" className="form-control-label">Left Title</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="lefttitle" value={value.lefttitle} onChange={update} />
          </Col>
          <Label md="2" className="form-control-label">Right Logo</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="rightlogo" placeholder="Logo URL" value={value.rightlogo} onChange={update} />
          </Col>
        </Row>
        <Row className="varible_container">
          <Label md="2" className="form-control-label">Right Value</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="rightvalue" value={value.rightvalue} onChange={update} />
          </Col>
          <Label md="2" className="form-control-label">Right Title</Label>
          <Col md="4">
            <input type="text" className="form-control form-control-sm" name="righttile" value={value.righttile} onChange={update} />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="bottomblockleft-container">
      <Row className="varible_container mb-3">
        <Label md="2" className="form-control-label">{fieldName}</Label>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Left Logo</Label>
        <Col md="10">
          <Input type="text" name="leftlogo" placeholder="Logo URL" value={value.leftlogo} onChange={update} />
        </Col>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Left Value</Label>
        <Col md="10">
          <Input type="text" name="leftvalue" value={value.leftvalue} onChange={update} />
        </Col>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Left Title</Label>
        <Col md="10">
          <Input type="text" name="lefttitle" value={value.lefttitle} onChange={update} />
        </Col>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Right Logo</Label>
        <Col md="10">
          <Input type="text" name="rightlogo" placeholder="Logo URL" value={value.rightlogo} onChange={update} />
        </Col>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Right Value</Label>
        <Col md="10">
          <Input type="text" name="rightvalue" value={value.rightvalue} onChange={update} />
        </Col>
      </Row>
      <Row className="varible_container">
        <Label md="2" className="form-control-label">Right Title</Label>
        <Col md="10">
          <Input type="text" name="righttile" value={value.righttile} onChange={update} />
        </Col>
      </Row>
    </div>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
BottomBlockLeftField.ownsChrome = true;
